import Image from 'next/image'
import { Row, Col, SlideShow, Anchor, colors } from '@microfrontends/react-components'
import { useRef, useState, useEffect, useMemo } from 'react'
import { ICompanyInfo } from '../CompanyDetail/type'
import {
    AspectRatioContainer,
    ImageWrapper,
    InformationItem,
    InformationList,
    AboutUsWrapper,
    ReadMoreButton,
    AboutUsDescription,
    CompanyImagePreview,
    H2
} from './AboutUs.css'
import { useStore } from '../../store'
import { TInfoCompanyOCI } from '../../services/companyDetailService'
import { stripHtml } from '../../helpers/common'

interface Props {
    t: (text: string) => string
    companyInfo: ICompanyInfo | null
    ociData: TInfoCompanyOCI | null | undefined
}
const DESCRIPTION_MAX_HEIGHT = 200
const OFFSET = 5
const EMPTY_IMAGE = `${process.env.NEXT_PUBLIC_IMAGE_URL}/company-assets/images/icon-workingoffice1x.png`
const GOOGLE_MAP_URL = 'https://www.google.com/maps'
const AboutUs: React.FC<Props> = ({ t, companyInfo, ociData }) => {
    const { isRobot } = useStore().state
    const {
        contactName: companyContactName,
        companyProfile,
        companySizeName,
        jobImageURLs,
        address: companyAddress
    } = companyInfo || {}
    const [canExtend, setCanExtend] = useState(false)
    const [extend, setExtend] = useState(false)
    const [descriptionContentHeight, setDescriptionContentHeight] = useState(0)
    const descriptionRef = useRef<HTMLParagraphElement>(null)
    const handleExtendDescription = () => {
        setExtend(!extend)
    }

    const isPublicOCI = companyInfo?.profilePublishedSiteMask === 2 || companyInfo?.profilePublishedSiteMask === 3

    let categories = companyInfo?.industryNames
    const companySize = ociData?.companySize || companySizeName

    const aboutUsImages = useMemo(() => {
        if (ociData?.companyImages && ociData?.companyImages.length > 0) {
            return ociData?.companyImages
        }
        if (jobImageURLs && jobImageURLs.length > 0) {
            return jobImageURLs.filter((image) => image !== '')
        }
        return []
    }, [jobImageURLs, ociData?.companyImages])

    const address = useMemo(() => {
        let tempAddress = ''

        if (ociData?.headquaterAddress) {
            tempAddress = ociData?.headquaterAddress
        } else {
            tempAddress = companyAddress || ''
        }

        return stripHtml(tempAddress)
    }, [companyAddress, ociData?.headquaterAddress])

    const contactName = useMemo(() => {
        if (ociData?.companyContact) {
            return ociData.companyContact
        }
        return companyContactName || null
    }, [ociData?.companyContact, companyContactName])

    useEffect(() => {
        if (descriptionRef && descriptionRef.current) {
            const textNode = descriptionRef.current.childNodes[0]
            if (textNode) {
                var range = document.createRange()
                range.selectNodeContents(textNode)
                const textNodeHeight = range.getBoundingClientRect().height + OFFSET
                if (textNodeHeight > DESCRIPTION_MAX_HEIGHT) {
                    setCanExtend(true)
                    setDescriptionContentHeight(textNodeHeight)
                }
            }
        }
    }, [companyInfo])

    /*
     * Check for company oci profile
     * */
    let getCompanyProfile = companyProfile
    if (isPublicOCI) {
        getCompanyProfile = ociData?.companyProfile || companyProfile
    }

    return (
        <AboutUsWrapper>
            <H2>{t('About Us')}</H2>
            <Row gutter={[24, 35]}>
                <Col xs={24}>
                    <InformationList>
                        {companySize && (
                            <InformationItem>
                                <p className='type'>{t('Size')}</p>
                                <p className='text'>{`${companySize} ${t('staffs')}`}</p>
                            </InformationItem>
                        )}
                        {categories && (
                            <InformationItem>
                                <p className='type'>{t('Industry')}</p>
                                <p className='text'>{categories}</p>
                            </InformationItem>
                        )}
                        {contactName && (
                            <InformationItem>
                                <p className='type'>{t('Contact')}</p>
                                <p className='text'>{contactName}</p>
                            </InformationItem>
                        )}
                        {address && (
                            <InformationItem>
                                <p className='type'>{t('Address')}</p>
                                <div className='text'>
                                    <div className='dangerously-text' dangerouslySetInnerHTML={{ __html: address }} />
                                    <Anchor
                                        name='paragraph'
                                        size='medium'
                                        href={`${GOOGLE_MAP_URL}?q=${address}`}
                                        target='_blank'
                                        color={colors.primary[40]}
                                        style={{ marginLeft: 8 }}
                                    >
                                        ({t('View map')})
                                    </Anchor>
                                </div>
                            </InformationItem>
                        )}
                    </InformationList>
                    <AboutUsDescription
                        ref={descriptionRef}
                        maxHeight={extend ? descriptionContentHeight : DESCRIPTION_MAX_HEIGHT}
                    >
                        {getCompanyProfile}
                    </AboutUsDescription>
                    {canExtend && (
                        <ReadMoreButton onClick={handleExtendDescription}>
                            {extend ? t('Show less') : t('Read more')}
                        </ReadMoreButton>
                    )}
                </Col>

                <Col xs={24}>
                    {aboutUsImages && aboutUsImages.length > 0 ? (
                        <div style={{ maxWidth: isPublicOCI ? 720 : '100%', margin: 'auto' }}>
                            <SlideShow
                                id='company-image'
                                slides={{ elementPerView: 1 }}
                                autoEnable
                                transitionTime={500}
                                autoTime={5000}
                                isRobot={isRobot}
                                pagination={{ maxShow: 5 }}
                            >
                                {aboutUsImages?.map((url, index) => (
                                    <AspectRatioContainer key={`${url}-${index}`} hasBorderRadius={true}>
                                        <ImageWrapper>
                                            <Image
                                                src={url}
                                                alt={companyInfo?.companyName ?? ''}
                                                layout='fill'
                                                objectFit='contain'
                                            />
                                        </ImageWrapper>
                                    </AspectRatioContainer>
                                ))}
                            </SlideShow>
                        </div>
                    ) : (
                        <CompanyImagePreview isEmpty>
                            <AspectRatioContainer hasBorderRadius>
                                <ImageWrapper>
                                    <Image
                                        src={EMPTY_IMAGE}
                                        alt={companyInfo?.companyName ?? ''}
                                        layout='fill'
                                        objectFit='contain'
                                    />
                                </ImageWrapper>
                            </AspectRatioContainer>
                        </CompanyImagePreview>
                    )}
                </Col>
            </Row>
        </AboutUsWrapper>
    )
}

export default AboutUs
